import React, { useState, useEffect } from 'react';
import { Header } from './Header';
import { NavMenu } from './NavMenu';
import { ErrorBoundary } from '../ui/ErrorBoundary';

interface ShellProps {
  children: React.ReactNode;
}

export const Shell: React.FC<ShellProps> = ({ children }) => {
  const [navCollapsed, setNavCollapsed] = useState(() => {
    const stored = sessionStorage.getItem('navCollapsed');
    return stored ? JSON.parse(stored) : false;
  });

  const [isMobile, setIsMobile] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });

  useEffect(() => {
    sessionStorage.setItem('navCollapsed', JSON.stringify(navCollapsed));
  }, [navCollapsed]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleNav = () => {
    setNavCollapsed(!navCollapsed);
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    width: '100%',
    backgroundColor: 'var(--color-white)',
  };

  const sidebarStyle: React.CSSProperties = {
    width: navCollapsed ? 64 : 240,
    transition: 'width 0.3s ease-in-out',
    flexShrink: 0,
    display: isMobile && navCollapsed ? 'none' : 'block',
  };

  const mainAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: 'var(--color-white)',
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#f5f5f5',
  };

  return (
    <div style={containerStyle}>
      <aside style={sidebarStyle}>
        <NavMenu isCollapsed={navCollapsed} onToggle={toggleNav} />
      </aside>
      <div style={mainAreaStyle}>
        <div style={headerStyle}>
          <Header onToggleNav={toggleNav} />
        </div>
        <main style={contentStyle}>
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
};
